import {
  Autocomplete,
  Badge,
  Button,
  Checkbox,
  Flex,
  Grid,
  Group,
  Modal,
  Select,
  Text,
  Textarea,
  TextInput,
  ThemeIcon,
} from '@mantine/core';
import { useForm } from '@mantine/form';
import { IconCheck, IconFileText, IconPlus, IconTrash } from '@tabler/icons';
import { DataTable } from 'mantine-datatable';
import { useEffect, useMemo, useRef, useState } from 'react';
import { ReceiptUserListItem } from '../../@types/daily-work';
import { CustomDatePicker } from '../../components/custom-date-picker';
import { CustomDateRangePicker } from '../../components/custom-date-range-picker';
import { CustomLoader } from '../../components/custom-loader';
import { Page } from '../../components/page';
import { getDailyWorkReceiptUserListRequest } from '../../data/daily-work';

import {
  useGenerateGenericCashFlowReceipt,
  useGetCashFlowTransactions,
} from '../../data/hooks/cash-flow';
import { useGetUser } from '../../data/hooks/user';
import { UserRole } from '../../models/user';
import { formatLocale } from '../../providers/dayjs-plugins';
import { errorNotification } from '../../providers/mantine-notifications';
import {
  cashFlowTypeColor,
  cashFlowTypeHumanized,
} from '../../utils/constants/cash-flow';
import { userRoleHumanized } from '../../utils/constants/user';
import { CashFlowTransactionType } from '../../utils/enums/cash-flow';
import { formatBRL, realToUSCash } from '../../utils/helpers';

function downloadFile(response: Blob, fileName: string) {
  const url = URL.createObjectURL(new Blob([response]));
  const link = document.createElement('a');
  link.href = url;
  link.setAttribute('download', fileName);
  document.body.appendChild(link);
  link.click();
  URL.revokeObjectURL(url);
}

type FormFilterType = {
  page: number;
  limit: number;
  createdAt: string;
  name: string;
  value: string;
  showCancelled: boolean;
};

type GenericReceiptValueState = {
  value: string;
  description: string;
};

type PageModalState = 'salary-receipt' | null;

export function SalaryCashFlowList() {
  const [pageLoading, setPageLoading] = useState(false);
  const [receiptUserList, setReceiptUserList] = useState<ReceiptUserListItem[]>(
    [],
  );
  const [selectReceiptUser, setSelectReceiptUser] = useState<
    ReceiptUserListItem[]
  >([]);
  const { loading, fetch, response } = useGetCashFlowTransactions();
  const {
    loading: genCashFlowReceiptLoader,
    fetch: genCashFlowReceiptFetcher,
  } = useGenerateGenericCashFlowReceipt();
  const { fetch: getUsersFetcher, response: getUsersResponse } = useGetUser();

  const [pageModal, setPageModal] = useState<PageModalState>(null);
  const [salaryReceiptValues, setSalaryReceiptValues] = useState<
    GenericReceiptValueState[]
  >([{ description: 'INITIAL', value: 'INITIAL' }]);
  const page = useRef(1);
  const limit = useRef(10);

  const formFilter = useForm<FormFilterType>({
    initialValues: {
      createdAt: '',
      name: '',
      value: '',
      showCancelled: false,
      limit: limit.current,
      page: page.current,
    },
  });

  const addSalaryReceiptForm = useForm({
    initialValues: {
      value: '',
      description: '',
      transactionNote: '',
      username: '',
      total: '0.00',
    },
  });

  const receiptListForm = useForm({
    initialValues: {
      user: '',
      dateBegin: new Date(),
      dateEnd: new Date(),
      role: 'monthly-employee',
    },
  });

  const totalGeneral = useMemo(() => {
    const baseTotal = selectReceiptUser.reduce(
      (acc, item) => acc + (item.totalPrice || 0),
      0,
    );
    const looseTotal = salaryReceiptValues.reduce((acc, item) => {
      if (item.description === 'INITIAL' && item.value === 'INITIAL') {
        return acc;
      }
      const val = Number(realToUSCash(item.value)) || 0;
      return acc + val;
    }, 0);
    return (baseTotal + looseTotal).toFixed(2);
  }, [selectReceiptUser, salaryReceiptValues]);

  function addSalaryReceiptValue(item: GenericReceiptValueState) {
    if (item.description !== '' && item.value !== '') {
      setSalaryReceiptValues([...salaryReceiptValues, item]);
    }
    addSalaryReceiptForm.setValues({
      description: '',
      value: '',
    });
  }

  function removeSalaryReceiptValue(description: string) {
    const cloneList = [...salaryReceiptValues];
    const findIndex = cloneList.findIndex(
      (item) => item.description === description,
    );

    if (findIndex !== -1) {
      cloneList.splice(findIndex, 1);
      setSalaryReceiptValues(cloneList);
    }
  }

  async function getTransactions(isReset = false) {
    const validatedValues: any = {};

    Object.entries(formFilter.values).forEach(([key, val]) => {
      if (val !== '' && val !== null && val !== undefined) {
        validatedValues[key] = val;
      }
    });

    if (isReset) {
      await fetch({
        params: {
          page: 1,
          limit: 10,
          showCancelled: false,
          type: CashFlowTransactionType.SALARY,
        },
      });
    } else {
      await fetch({
        params: {
          ...validatedValues,
          createdAt:
            validatedValues['createdAt']?.[0] !== null
              ? validatedValues['createdAt']?.join(',')
              : undefined,
          value: validatedValues['value']
            ? realToUSCash(validatedValues['value'])
            : undefined,
          page: page.current,
          limit: limit.current,
          type: CashFlowTransactionType.SALARY,
        },
      });
    }
  }

  async function handlePageChange(newPage: number) {
    page.current = newPage;
    getTransactions();
  }

  async function handlePageLimitChange(newLimit: number) {
    limit.current = newLimit;
    getTransactions();
  }

  async function handleClearFilter() {
    formFilter.reset();
    page.current = 1;
    await getTransactions(true);
  }

  async function getUsers() {
    await getUsersFetcher();
  }

  async function handleCreateSalaryReceipt() {
    const clonedList = [...salaryReceiptValues];
    clonedList.splice(0, 1); // remove 'INITIAL'

    if (selectReceiptUser.length === 0 && clonedList.length === 0) {
      errorNotification({
        title: 'Seleção vazia',
        message:
          'Por favor, selecione pelo menos um funcionário na lista ou adicione um item avulso.',
      });
      return;
    }

    const selectedUserFromDropdown = getUsersResponse?.find(
      (u) => String(u.id) === receiptListForm.values.user,
    );
    const username =
      selectReceiptUser[0]?.username ||
      selectedUserFromDropdown?.username ||
      '';

    const monthNames = [
      'Janeiro',
      'Fevereiro',
      'Março',
      'Abril',
      'Maio',
      'Junho',
      'Julho',
      'Agosto',
      'Setembro',
      'Outubro',
      'Novembro',
      'Dezembro',
    ];
    const monthName = monthNames[receiptListForm.values.dateBegin.getMonth()];
    const year = receiptListForm.values.dateBegin.getFullYear();

    const employeeItems = selectReceiptUser.map((item) => ({
      description: `Salário - ${monthName}/${year}`,
      value: item.totalPrice.toFixed(2),
    }));

    const looseItems = clonedList.map((item) => ({
      description: item.description,
      value: realToUSCash(item.value),
    }));

    const userIds =
      selectReceiptUser.length > 0
        ? Array.from(new Set(selectReceiptUser.map((item) => item.id)))
        : undefined;

    await genCashFlowReceiptFetcher({
      data: {
        total: totalGeneral,
        username,
        type: CashFlowTransactionType.SALARY,
        transactionNote: addSalaryReceiptForm.values.transactionNote || '-',
        list: [...employeeItems, ...looseItems],
        dateBegin: receiptListForm.values.dateBegin.toISOString(),
        dateEnd: receiptListForm.values.dateEnd.toISOString(),
        dailyWorkUsers: userIds,
      },
      onSuccess: (res) => {
        downloadFile(
          res,
          `recibo-salario-${String(new Date(Date.now()).valueOf()).slice(
            7,
            11,
          )}.pdf`,
        );
        setSalaryReceiptValues([{ description: 'INITIAL', value: 'INITIAL' }]);
        addSalaryReceiptForm.reset();
        setPageModal(null);
        getTransactions();
        setSelectReceiptUser([]);
        setReceiptUserList([]);
      },
    });
  }

  async function handleFilterReceiptList({
    dateBegin,
    dateEnd,
    role,
    user,
  }: typeof receiptListForm.values) {
    try {
      setPageLoading(true);
      const response = await getDailyWorkReceiptUserListRequest({
        dateBegin: dateBegin.toISOString(),
        dateEnd: dateEnd.toISOString(),
        role: role ? (role as UserRole) : undefined,
        userId: user ? Number(user) : undefined,
      });
      setReceiptUserList(response);
      setPageLoading(false);
    } catch (error) {
      setPageLoading(false);
      errorNotification({
        title: 'Erro ao buscar usuários.',
        message: 'tente novamente',
      });
    }
  }

  useEffect(() => {
    getTransactions();
    getUsers();
  }, []);

  return (
    <Page title="Movimentação de Salários">
      <CustomLoader loading={loading || genCashFlowReceiptLoader} />
      <Flex m={16} direction="column">
        <Flex align="center" justify="end">
          <Button
            color="ltpBlue.7"
            leftIcon={<IconFileText size={16} />}
            onClick={() => setPageModal('salary-receipt')}
          >
            Lançar Salário
          </Button>
        </Flex>
        <Flex wrap="wrap" align="center" justify="start" mb={8}>
          <form
            onSubmit={formFilter.onSubmit(() => {
              getTransactions();
            })}
          >
            <Flex align="center" wrap="wrap">
              <CustomDateRangePicker
                required
                withAsterisk
                placeholder="selecione um intervalo"
                label="Intervalo"
                maw={170}
                mb={16}
                mr={8}
                name="createdAt"
                {...formFilter.getInputProps('createdAt')}
              />
              <Autocomplete
                label="Usuário"
                placeholder="comece a digitar um nome"
                data={getUsersResponse?.map((item) => item.username) ?? []}
                mb={16}
                mr={8}
                {...formFilter.getInputProps('name')}
              />
              <TextInput
                label="Valor"
                placeholder="valor"
                type="text"
                mb={16}
                mr={8}
                value={formFilter.values.value}
                onChange={(e) =>
                  formFilter.setFieldValue('value', formatBRL(e.target.value))
                }
              />
              <Button mb={-9} color="ltpBlue.9" onClick={handleClearFilter}>
                Limpar
              </Button>
              <Button mb={-9} ml={16} type="submit">
                Filtrar
              </Button>
              <Checkbox
                label="Cancelados"
                name="showCancelled"
                color="blue"
                ml={8}
                mb={-5}
                checked={formFilter.values.showCancelled}
                {...formFilter.getInputProps('showCancelled')}
              />
            </Flex>
          </form>
        </Flex>
        <DataTable
          recordsPerPage={response?.meta.itemsPerPage ?? 5}
          recordsPerPageOptions={[10, 50, 100, 500]}
          onRecordsPerPageChange={(recordRange) =>
            handlePageLimitChange(recordRange)
          }
          onPageChange={handlePageChange}
          totalRecords={response?.meta.totalItems}
          page={page.current}
          fetching={loading}
          height="72vh"
          noRecordsText="Sem movimentação"
          withBorder
          borderRadius="sm"
          striped
          highlightOnHover
          records={response?.items}
          rowStyle={({ cancelledAt }) =>
            cancelledAt && {
              backgroundColor: 'rgba(255, 0, 0, 0.055)',
              color: 'red',
            }
          }
          columns={[
            {
              accessor: 'username',
              title: 'Funcionário',
            },
            {
              accessor: 'type',
              title: 'Tipo',
              render: ({ type }) => (
                <Badge color={cashFlowTypeColor[type]}>
                  {cashFlowTypeHumanized[type]}
                </Badge>
              ),
            },
            {
              accessor: 'status',
              title: '',
              render: ({ cancelledAt }) =>
                cancelledAt && <Badge color="red.7">Cancelado</Badge>,
            },
            {
              accessor: 'value',
              title: 'Valor',
              render: ({ value, cancelledAt }) => (
                <Text
                  fw="bold"
                  color={Number(value) < 0 ? 'red.7' : 'green.7'}
                  style={{ textDecoration: cancelledAt && 'line-through' }}
                >{`${formatBRL(String(value))}`}</Text>
              ),
            },
            {
              accessor: 'name',
              title: 'Responsável/Solicitante',
            },
            {
              accessor: 'description',
              title: 'Descrição',
            },
            {
              accessor: 'createdAt',
              title: 'Data da Operação',
              render: ({ createdAt }) =>
                createdAt ? formatLocale(createdAt, 'DD/MM/YYYY HH:mm') : '--',
            },
            {
              accessor: 'createdAt',
              title: 'Cancelado em',
              render: ({ cancelledAt }) =>
                cancelledAt
                  ? formatLocale(cancelledAt, 'DD/MM/YYYY HH:mm')
                  : '--',
            },
            {
              accessor: 'createdAt',
              title: 'Cancelado por',
              render: ({ cancelledBy }) => cancelledBy,
            },
          ]}
        />
      </Flex>

      <Modal
        size={'60%'}
        title="Lançar Salário"
        opened={pageModal === 'salary-receipt'}
        onClose={() => {
          setPageModal(null);
          addSalaryReceiptForm.reset();
          setSalaryReceiptValues([
            { description: 'INITIAL', value: 'INITIAL' },
          ]);
          setSelectReceiptUser([]);
          setReceiptUserList([]);
        }}
        closeOnClickOutside={false}
        closeOnEscape={false}
      >
        <form
          onSubmit={receiptListForm.onSubmit((values) =>
            handleFilterReceiptList(values),
          )}
        >
          <Flex align="center">
            <Select
              required
              withAsterisk
              label="Funcionário"
              placeholder="Selecione um funcionário"
              searchable
              data={
                getUsersResponse?.map((item) => ({
                  label: item.username,
                  value: String(item.id),
                })) ?? []
              }
              {...receiptListForm.getInputProps('user')}
              w={350}
              mb={16}
              mr={8}
            />
            <CustomDatePicker
              required
              withAsterisk
              placeholder="selecione uma data"
              label="Data Inicial"
              maw={170}
              mb={16}
              mr={8}
              {...receiptListForm.getInputProps('dateBegin')}
            />
            <CustomDatePicker
              required
              withAsterisk
              placeholder="selecione uma data"
              label="Data Final"
              maw={170}
              mb={16}
              mr={8}
              {...receiptListForm.getInputProps('dateEnd')}
            />
            <Button mb={-10} type="submit">
              Buscar
            </Button>
          </Flex>
        </form>
        <DataTable
          selectedRecords={selectReceiptUser}
          onSelectedRecordsChange={setSelectReceiptUser}
          fetching={pageLoading}
          minHeight={200}
          height={300}
          noRecordsText="Sem usuários"
          withBorder
          borderRadius="sm"
          striped
          highlightOnHover
          records={receiptUserList}
          columns={[
            {
              accessor: 'username',
              title: 'Funcionário',
            },
            {
              accessor: 'userRole',
              title: 'Tipo',
              render: ({ role }) => userRoleHumanized[role],
            },
            {
              accessor: 'transportPrice',
              title: 'Horas trabalhadas',
              render: ({ workMinutes }) => {
                const restMinutes = workMinutes % 60;
                const hours = (workMinutes - restMinutes) / 60;

                return `${hours}hr ${restMinutes}min`;
              },
            },
            {
              accessor: 'transportPrice',
              title: 'Valor Diária',
              render: ({ dailyPrice }) => formatBRL(dailyPrice?.toFixed(2)),
            },
            {
              accessor: 'transportPrice',
              title: 'Valor total',
              render: ({ totalPrice }) => formatBRL(totalPrice?.toFixed(2)),
            },
            {
              accessor: '',
              title: '',
              render: ({ isPaid }) =>
                isPaid && (
                  <ThemeIcon title="ja emitido" variant="light" color="green">
                    <IconCheck />
                  </ThemeIcon>
                ),
            },
          ]}
        />
        <form
          onSubmit={addSalaryReceiptForm.onSubmit(handleCreateSalaryReceipt)}
        >
          <Grid columns={2} mt="md">
            <Grid.Col span={2}>
              <DataTable
                minHeight={200}
                noRecordsText="Sem valores"
                withBorder
                borderRadius="sm"
                striped
                highlightOnHover
                records={salaryReceiptValues}
                columns={[
                  {
                    accessor: 'description',
                    title: 'Descrição',
                    render: ({ description }) =>
                      description === 'INITIAL' ? (
                        <TextInput
                          placeholder="descrição"
                          type="text"
                          name="description"
                          {...addSalaryReceiptForm.getInputProps('description')}
                        />
                      ) : (
                        description
                      ),
                  },
                  {
                    accessor: 'value',
                    title: 'Valor',
                    render: ({ value }) =>
                      value === 'INITIAL' ? (
                        <TextInput
                          placeholder="valor"
                          type="text"
                          name="value"
                          value={addSalaryReceiptForm.values.value}
                          onChange={(e) =>
                            addSalaryReceiptForm.setFieldValue(
                              'value',
                              formatBRL(e.target.value),
                            )
                          }
                        />
                      ) : (
                        value
                      ),
                    width: 120,
                  },
                  {
                    accessor: 'action',
                    title: '',
                    render: ({ description }) =>
                      description === 'INITIAL' ? (
                        <Button
                          type="button"
                          color="blue"
                          variant="light"
                          p={10}
                          onClick={() =>
                            addSalaryReceiptValue(addSalaryReceiptForm.values)
                          }
                        >
                          <IconPlus size={15} />
                        </Button>
                      ) : (
                        <Button
                          type="button"
                          color="red"
                          variant="light"
                          p={10}
                          onClick={() => removeSalaryReceiptValue(description)}
                        >
                          <IconTrash size={15} />
                        </Button>
                      ),
                  },
                ]}
              />
            </Grid.Col>
            <Grid.Col span={2} style={{ textAlign: 'end' }}>
              <Text>Total</Text>
              <Text size={28} fw="bold">
                {formatBRL(totalGeneral)}
              </Text>
            </Grid.Col>
            <Grid.Col span={2}>
              <Textarea
                maxLength={255}
                autosize
                label="Detalhes"
                placeholder="obs que motivou retirada"
                {...addSalaryReceiptForm.getInputProps('transactionNote')}
              />
            </Grid.Col>
          </Grid>
          <Group mt={16} mb={16} position="right">
            <Button type="submit">Lançar Salário</Button>
          </Group>
        </form>
      </Modal>
    </Page>
  );
}
